import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import DeleteIcon from '@src/svgs/DeleteIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { FC, MouseEvent } from 'react';

type Props = {
  onEdit: () => void;
  onRemove: () => void;
  /** When provided (dubbing enabled), shows a "Manage AI Voices" item. */
  onManageAiVoices?: () => void;
};

const MENU_WIDTH = 220;
const MENU_ITEM_HEIGHT = 48;

/**
 * Kebab actions menu for a speaker row (Edit / Remove from Organization).
 * Mirrors the Story Manager row-actions pattern: a portaled, viewport-clamped
 * surface that closes on outside-click or Escape.
 */
const SpeakerRowActionsMenu: FC<Props> = ({
  onEdit,
  onRemove,
  onManageAiVoices,
}) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setPos(null), []);
  useOutsideAlerter(portalRef, close);

  useEffect(() => {
    if (!pos) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pos, close]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (pos) {
      close();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuHeight = MENU_ITEM_HEIGHT * (onManageAiVoices ? 3 : 2);
    setPos(
      clampMenuToViewport(
        rect.right - MENU_WIDTH,
        rect.bottom + 4,
        MENU_WIDTH,
        menuHeight,
      ),
    );
  };

  const wrap = (cb: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    close();
    cb();
  };

  return (
    <>
      <KebabButton
        onClick={handleClick}
        data-testid="speaker-row-actions-trigger"
        aria-label="Speaker actions"
      >
        <EllipsisIcon fillColor="currentColor" />
      </KebabButton>
      {pos &&
        createPortal(
          <ContextMenuSurface
            ref={portalRef}
            style={{ top: pos.y, left: pos.x }}
            data-testid="speaker-row-actions-menu"
          >
            <ContextMenuItem
              onClick={wrap(onEdit)}
              data-testid="speaker-action-edit"
            >
              <PencilIcon strokeColor="currentColor" /> Edit
            </ContextMenuItem>
            {onManageAiVoices && (
              <ContextMenuItem
                onClick={wrap(onManageAiVoices)}
                data-testid="speaker-action-manage-ai-voices"
              >
                <MicrophoneIcon width="14" height="14" /> Manage AI Voices
              </ContextMenuItem>
            )}
            <ContextMenuItem
              $danger
              onClick={wrap(onRemove)}
              data-testid="speaker-action-remove"
            >
              <DeleteIcon /> Remove from Organization
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

export default SpeakerRowActionsMenu;
